import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { typesLabel, pricingLabel, WERK_TYPES } from "@/lib/jobOptions";
import { MapPin, Euro, Calendar, Filter, BadgeCheck, Inbox } from "lucide-react";
import { toast } from "sonner";

export function ReageerOpdrachten() {
  const [jobs, setJobs] = useState([]);
  const [typeFilter, setTypeFilter] = useState("alle");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (typeFilter !== "alle") params.type_werk = typeFilter;
      const { data } = await api.get("/jobs/beschikbaar", { params });
      setJobs(data.jobs);
    } catch (e) { toast.error("Opdrachten laden mislukt"); }
  }, [typeFilter]);
  useEffect(() => { load(); }, [load]);

  const showInterest = async (id) => {
    try {
      await api.post(`/jobs/${id}/interesse`);
      toast.success("Je interesse is doorgegeven aan de opdrachtgever!");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reageren mislukt"); }
  };

  return (
    <div className="space-y-5" data-testid="reageer-opdrachten-tab">
      <p className="text-sm text-slate-500">Reageer als vakman op opdrachten van andere opdrachtgevers. Toon interesse en de opdrachtgever neemt contact met je op.</p>
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3">
        <div className="w-64">
          <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Filter op type werk</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="reageer-filter-type" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle types</SelectItem>
              {WERK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <span data-testid="reageer-jobs-count" className="text-xs font-semibold text-slate-500 ml-auto">{jobs.length} opdrachten</span>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />Geen open opdrachten beschikbaar.</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <div key={job.id} data-testid="reageer-job-card" className="bg-white border border-slate-200 rounded-md p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-display font-bold text-slate-900 leading-tight">{job.titel}</h3>
                <StatusBadge status={job.status} />
              </div>
              <p className="text-sm text-slate-600 mb-3 line-clamp-3">{job.beschrijving}</p>
              <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                <p className="flex items-center gap-1.5 font-semibold text-slate-700">
                  {job.company_name}
                  {job.company_geverifieerd && <BadgeCheck className="w-3.5 h-3.5 text-green-600" />}
                </p>
                <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {job.provincie || job.locatie || "—"}</p>
                <p className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {typesLabel(job.types)}</p>
                <p className="flex items-center gap-1.5"><Euro className="w-3.5 h-3.5" /> {pricingLabel(job)}</p>
              </div>
              <div className="mt-auto">
                {job.mijn_interesse ? (
                  <Button data-testid="reageer-interesse-getoond" disabled className="w-full bg-green-100 text-green-700 rounded-sm font-bold cursor-default hover:bg-green-100">✓ Interesse getoond</Button>
                ) : (
                  <Button data-testid="reageer-interesse-btn" onClick={() => showInterest(job.id)}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold text-sm leading-tight whitespace-normal h-auto py-2.5">
                    Laat weten dat je interesse hebt
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
